import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import { api } from '../../utils/api';

const FONT_OPTIONS = [
  { value: 'system-ui', label: 'System' },
  { value: 'Georgia, serif', label: 'Georgia' },
  { value: 'sans-serif', label: 'Sans Serif' },
  { value: '"Inter", sans-serif', label: 'Inter' },
  { value: '"Roboto", sans-serif', label: 'Roboto' },
  { value: '"Open Sans", sans-serif', label: 'Open Sans' },
  { value: '"Playfair Display", serif', label: 'Playfair Display' },
  { value: '"Lora", serif', label: 'Lora' },
  { value: '"Montserrat", sans-serif', label: 'Montserrat' },
  { value: '"Oswald", sans-serif', label: 'Oswald' },
];

const GOOGLE_FONTS = ['Inter', 'Roboto', 'Open Sans', 'Playfair Display', 'Lora', 'Montserrat', 'Oswald'];

export default function MenuTheme() {
  const { getToken } = useAuth();
  const navigate = useNavigate();
  const [business, setBusiness] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [menuBackgroundColor, setMenuBackgroundColor] = useState('#ffffff');
  const [menuBackgroundImageUrl, setMenuBackgroundImageUrl] = useState('');
  const [menuFont, setMenuFont] = useState('system-ui');
  const [menuTextColor, setMenuTextColor] = useState('#1e293b');
  const [menuImagePosition, setMenuImagePosition] = useState('top');
  const [menuCategoryTileColor, setMenuCategoryTileColor] = useState('');
  const [menuCategoryInactiveTileColor, setMenuCategoryInactiveTileColor] = useState('');
  const [menuCategoryTextColor, setMenuCategoryTextColor] = useState('');
  const [uploadingBg, setUploadingBg] = useState(false);
  const [previewCatId, setPreviewCatId] = useState(null);

  const fetchBusiness = useCallback(async () => {
    const res = await api('/api/owners/me/business', { getToken });
    if (!res.ok) {
      if (res.status === 403) {
        navigate('/', { replace: true });
        return null;
      }
      setError('Failed to load business');
      return null;
    }
    const data = await res.json();
    setBusiness(data);
    setMenuBackgroundColor(data.menuBackgroundColor || '#ffffff');
    setMenuBackgroundImageUrl(data.menuBackgroundImageUrl || '');
    setMenuFont(data.menuFont || 'system-ui');
    setMenuTextColor(data.menuTextColor || '#1e293b');
    setMenuImagePosition(data.menuImagePosition || 'top');
    setMenuCategoryTileColor(data.menuCategoryTileColor || '');
    setMenuCategoryInactiveTileColor(data.menuCategoryInactiveTileColor || '');
    setMenuCategoryTextColor(data.menuCategoryTextColor || '');
    setError(null);
    return data;
  }, [getToken, navigate]);

  const fetchMenu = useCallback(async () => {
    const res = await api('/api/owners/me/menu', { getToken });
    if (res.ok) {
      const d = await res.json();
      setCategories(d.categories || []);
      if (d.categories?.length && !previewCatId) setPreviewCatId(d.categories[0].id);
    }
  }, [getToken, previewCatId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      await Promise.all([fetchBusiness(), fetchMenu()]);
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [fetchBusiness, fetchMenu]);

  const handleBgImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingBg(true);
    setError(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api('/api/owners/me/upload-image', {
        getToken,
        method: 'POST',
        body: formData,
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || `Error ${res.status}`);
      if (d.url) setMenuBackgroundImageUrl(d.url);
    } catch (err) {
      setError(err.message);
    } finally {
      setUploadingBg(false);
    }
    e.target.value = '';
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const res = await api('/api/owners/me/business/theme', {
        getToken,
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          menuBackgroundColor,
          menuBackgroundImageUrl: menuBackgroundImageUrl || '',
          menuFont,
          menuTextColor,
          menuImagePosition,
          menuCategoryTileColor: menuCategoryTileColor || '',
          menuCategoryInactiveTileColor: menuCategoryInactiveTileColor || '',
          menuCategoryTextColor: menuCategoryTextColor || '',
        }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || `Error ${res.status}`);
      await fetchBusiness();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading && !business) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <p className="text-slate-600 dark:text-slate-400">Loading…</p>
      </div>
    );
  }

  const baseUrl = process.env.REACT_APP_API_URL || '';
  const resolveImg = (url) =>
    url?.startsWith('/') && baseUrl ? `${baseUrl.replace(/\/$/, '')}${url}` : url;

  const bgImageSrc = menuBackgroundImageUrl?.startsWith('/')
    ? (baseUrl ? `${baseUrl.replace(/\/$/, '')}${menuBackgroundImageUrl}` : menuBackgroundImageUrl)
    : menuBackgroundImageUrl;

  const fontName = menuFont.split(',')[0]?.replace(/"/g, '').trim() || '';
  const needsGoogleFont = GOOGLE_FONTS.includes(fontName);

  const activeCat = categories.find((c) => c.id === previewCatId) || categories[0];
  const pos = menuImagePosition;
  const isVertical = pos === 'top' || pos === 'bottom';

  const inactiveTileBg = menuCategoryInactiveTileColor || '#f8fafc';
  const catTileText = menuCategoryTextColor || menuTextColor || '#475569';
  const activeTileBg = menuCategoryTileColor || '#c9a227';
  const activeTileText = menuCategoryTextColor || '#1a1a1a';
  const glowColor = menuCategoryTileColor || '#c9a227';

  return (
    <div className="space-y-6 pb-32">
      {needsGoogleFont && (
        <link
          href={`https://fonts.googleapis.com/css2?family=${fontName.replace(/\s+/g, '+')}&display=swap`}
          rel="stylesheet"
        />
      )}
      <div>
        <Link to="/owner/dashboard" className="text-sm text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 mb-1 inline-block">
          ← Back to dashboard
        </Link>
        <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">
          Menu theme
        </h1>
      </div>

      <div className="space-y-6">
        {/* Background color */}
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
            Background color
          </label>
          <div className="flex gap-3">
            <input
              type="color"
              value={menuBackgroundColor}
              onChange={(e) => setMenuBackgroundColor(e.target.value)}
              className="w-12 h-12 rounded-lg border border-slate-300 dark:border-slate-600 cursor-pointer p-1"
            />
            <input
              type="text"
              value={menuBackgroundColor}
              onChange={(e) => setMenuBackgroundColor(e.target.value)}
              className="flex-1 px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800"
            />
          </div>
        </div>

        {/* Background image */}
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
            Background image
          </label>
          <div className="flex gap-3">
            {bgImageSrc && (
              <img src={bgImageSrc} alt="" className="w-16 h-16 rounded object-cover" />
            )}
            <label className="inline-block px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700 cursor-pointer">
              <input
                type="file"
                accept="image/*"
                onChange={handleBgImageUpload}
                disabled={uploadingBg}
                className="hidden"
              />
              {uploadingBg ? '…' : 'Upload'}
            </label>
            {bgImageSrc && (
              <button
                type="button"
                onClick={() => setMenuBackgroundImageUrl('')}
                className="text-sm text-red-600"
              >
                Remove
              </button>
            )}
          </div>
        </div>

        {/* Font */}
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
            Font
          </label>
          <select
            value={menuFont}
            onChange={(e) => setMenuFont(e.target.value)}
            className="w-full px-4 py-3 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800"
          >
            {FONT_OPTIONS.map((f) => (
              <option key={f.value} value={f.value}>
                {f.label}
              </option>
            ))}
          </select>
        </div>

        {/* Text color */}
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
            Text color
          </label>
          <div className="flex gap-3">
            <input
              type="color"
              value={menuTextColor}
              onChange={(e) => setMenuTextColor(e.target.value)}
              className="w-12 h-12 rounded-lg border border-slate-300 dark:border-slate-600 cursor-pointer p-1"
            />
            <input
              type="text"
              value={menuTextColor}
              onChange={(e) => setMenuTextColor(e.target.value)}
              className="flex-1 px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800"
            />
          </div>
        </div>

        {/* Category tile color */}
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
            Category tile color
          </label>
          <div className="flex gap-3">
            <input
              type="color"
              value={menuCategoryTileColor || '#c9a227'}
              onChange={(e) => setMenuCategoryTileColor(e.target.value)}
              className="w-12 h-12 rounded-lg border border-slate-300 dark:border-slate-600 cursor-pointer p-1"
            />
            <input
              type="text"
              value={menuCategoryTileColor}
              onChange={(e) => setMenuCategoryTileColor(e.target.value)}
              placeholder="Default gold (#c9a227)"
              className="flex-1 px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800"
            />
            {menuCategoryTileColor && (
              <button type="button" onClick={() => setMenuCategoryTileColor('')} className="text-sm text-red-600">Reset</button>
            )}
          </div>
        </div>

        {/* Inactive category tile color */}
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
            Inactive category tile color
          </label>
          <div className="flex gap-3">
            <input
              type="color"
              value={menuCategoryInactiveTileColor || '#f8fafc'}
              onChange={(e) => setMenuCategoryInactiveTileColor(e.target.value)}
              className="w-12 h-12 rounded-lg border border-slate-300 dark:border-slate-600 cursor-pointer p-1"
            />
            <input
              type="text"
              value={menuCategoryInactiveTileColor}
              onChange={(e) => setMenuCategoryInactiveTileColor(e.target.value)}
              placeholder="Default light (#f8fafc)"
              className="flex-1 px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800"
            />
            {menuCategoryInactiveTileColor && (
              <button type="button" onClick={() => setMenuCategoryInactiveTileColor('')} className="text-sm text-red-600">Reset</button>
            )}
          </div>
        </div>

        {/* Category text color */}
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
            Category text color
          </label>
          <div className="flex gap-3">
            <input
              type="color"
              value={menuCategoryTextColor || '#1a1a1a'}
              onChange={(e) => setMenuCategoryTextColor(e.target.value)}
              className="w-12 h-12 rounded-lg border border-slate-300 dark:border-slate-600 cursor-pointer p-1"
            />
            <input
              type="text"
              value={menuCategoryTextColor}
              onChange={(e) => setMenuCategoryTextColor(e.target.value)}
              placeholder="Default dark (#1a1a1a)"
              className="flex-1 px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800"
            />
            {menuCategoryTextColor && (
              <button type="button" onClick={() => setMenuCategoryTextColor('')} className="text-sm text-red-600">Reset</button>
            )}
          </div>
        </div>

        {/* Item image position */}
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
            Item image position
          </label>
          <div className="grid grid-cols-4 gap-2">
            {[
              { value: 'top', label: 'Top' },
              { value: 'left', label: 'Left' },
              { value: 'right', label: 'Right' },
              { value: 'bottom', label: 'Bottom' },
            ].map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setMenuImagePosition(opt.value)}
                className={`py-3 rounded-lg text-sm font-medium border transition-colors ${
                  menuImagePosition === opt.value
                    ? 'bg-amber-500 text-slate-900 border-amber-500'
                    : 'border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {error && (
          <div className="p-4 rounded-lg bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300">
            {error}
          </div>
        )}

        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="w-full py-4 rounded-xl bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-slate-900 font-medium"
        >
          {saving ? 'Saving…' : 'Save theme'}
        </button>
      </div>

      {/* ===== TRUE LIVE PREVIEW ===== */}
      <div>
        <p className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
          Live preview
        </p>
        <div
          className="rounded-xl overflow-hidden border-2 border-slate-200 dark:border-slate-700 relative"
          style={{ maxHeight: 520 }}
        >
          {/* Phone-like frame */}
          <div
            className="relative overflow-hidden flex flex-col"
            style={{
              backgroundColor: menuBackgroundColor,
              fontFamily: menuFont,
              color: menuTextColor,
              minHeight: 400,
              maxHeight: 520,
            }}
          >
            {bgImageSrc && (
              <div
                className="absolute inset-0 pointer-events-none"
                style={{ backgroundImage: `url(${bgImageSrc})`, backgroundSize: 'cover', backgroundPosition: 'center' }}
              />
            )}

            {/* Header */}
            <div className="relative z-10 flex-shrink-0 pt-4 pb-2 px-4 text-center border-b border-white/10">
              {business?.logoUrl && (
                <img src={resolveImg(business.logoUrl)} alt="" className="w-12 h-12 rounded-full object-cover ring-2 ring-white/80 shadow-md mx-auto mb-2" />
              )}
              <h2 className="text-xl font-bold" style={{ color: menuTextColor }}>
                {business?.name || 'Your Business'}
              </h2>
              <span className="inline-block mt-1 px-2 py-0.5 text-[10px] font-semibold rounded-full bg-emerald-100 text-emerald-800">
                Open
              </span>
            </div>

            {/* Category tabs */}
            {categories.length > 0 && (
              <div className="relative z-10 flex-shrink-0 px-4 py-2">
                <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
                  {categories.map((cat) => {
                    const isActive = activeCat?.id === cat.id;
                    return (
                      <button
                        key={cat.id}
                        type="button"
                        onClick={() => setPreviewCatId(cat.id)}
                        className="flex-shrink-0 px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap border transition-all"
                        style={isActive
                          ? {
                              backgroundColor: activeTileBg,
                              color: activeTileText,
                              borderColor: glowColor,
                              boxShadow: `0 0 0 2px ${glowColor}, 0 0 12px ${glowColor}66`,
                            }
                          : { backgroundColor: inactiveTileBg, color: catTileText, borderColor: '#e2e8f0', boxShadow: 'none' }
                        }
                      >
                        {cat.name}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Items */}
            <div className="relative z-10 flex-1 overflow-y-auto px-4 pb-4 space-y-3">
              {activeCat?.items?.map((item) => {
                const imgSrc = resolveImg(item.imageUrl);
                const imgBlock = imgSrc ? (
                  <div className={isVertical ? 'w-full h-32' : 'flex-shrink-0 w-24 h-24'}>
                    <img src={imgSrc} alt="" className="w-full h-full object-cover" style={{ borderRadius: isVertical ? 0 : 8 }} />
                  </div>
                ) : null;

                const previewTextColor = menuTextColor || '#1a1a1a';
                const content = (
                  <div className={isVertical ? 'p-3' : 'flex-1 min-w-0 py-1'}>
                    <p className="font-semibold text-sm" style={{ color: previewTextColor }}>{item.name}</p>
                    {item.description && <p className="text-xs opacity-80 line-clamp-2 mt-0.5" style={{ color: previewTextColor }}>{item.description}</p>}
                    <p className="text-sm font-semibold mt-1" style={{ color: previewTextColor }}>${Number(item.price).toFixed(2)}</p>
                    {item.addons?.length > 0 && (
                      <p className="text-[10px] opacity-75 mt-1 uppercase tracking-wider" style={{ color: previewTextColor }}>
                        +{item.addons.length} add-on{item.addons.length !== 1 ? 's' : ''}
                      </p>
                    )}
                  </div>
                );

                const cardStyle = {
                  background: 'rgba(255,255,255,0.92)',
                  border: '1px solid rgba(0,0,0,0.06)',
                  borderRadius: 12,
                  overflow: 'hidden',
                };

                if (pos === 'left' || pos === 'right') {
                  return (
                    <div key={item.id} className="flex gap-3" style={cardStyle}>
                      {pos === 'left' && imgBlock}
                      {content}
                      {pos === 'right' && imgBlock}
                    </div>
                  );
                }
                return (
                  <div key={item.id} className="flex flex-col" style={cardStyle}>
                    {pos === 'top' && imgBlock}
                    {content}
                    {pos === 'bottom' && imgBlock}
                  </div>
                );
              })}
              {(!activeCat?.items || activeCat.items.length === 0) && (
                <p className="text-sm opacity-75 text-center py-8" style={{ color: menuTextColor }}>No items in this category</p>
              )}
            </div>

            {/* Bottom nav preview */}
            <div className="relative z-10 flex-shrink-0 bg-white border-t border-slate-200 flex items-center justify-around h-12 px-2">
              <span className="text-xs text-slate-400 flex flex-col items-center gap-0.5">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M3 3h2l.4 2M7 13h10l4-8H5.4" /></svg>
                Cart
              </span>
              <span className="text-xs text-slate-400 flex flex-col items-center gap-0.5">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684" /></svg>
                Share
              </span>
              <span className="text-xs text-slate-400 flex flex-col items-center gap-0.5">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6z" /></svg>
                Dashboard
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
